import type { trackEcommerce, trackEvent } from '@svs/nova-core/nextjs/analytics';
import type { Role } from '@svs/nova-core/nextjs/auth';
import type { DialogConfig } from '@svs/nova-foundation/nextjs/dialog';

import type { CouponId, CouponIndex, CouponType } from '../../../../schema/coupon/entry.js';
import type { WalletType } from '../../../../schema/frameworks/entry.js';
import type { Commission } from '../../../../schema/input/entry.js';
import type { ActivatedDrawId } from '../../../../schema/marketplace/entry.js';
import type {
  ICreateWagerUseCase,
  IDeleteCouponUseCase,
  IDeleteCouponWalletUseCase,
  IUpdateCouponWalletUseCase,
} from '../../../domain/entry.js';

export type ICreateWagerController = ReturnType<typeof createWagerController>;

type ControllerArgs = {
  wallets: Record<CouponIndex, WalletType | ActivatedDrawId> | undefined;
  couponId: CouponId;
  couponIndices: CouponIndex[];
  showDialog: (props: DialogConfig, meta?: string[]) => Promise<void>;
  paymentContext: {
    role?: Role;
    commissionPercent?: Commission;
    activatedDrawId?: ActivatedDrawId;
    paymentUrls?: Record<string, string | undefined>;
    marketplaceWallets?: Record<ActivatedDrawId, { balance: number; soldShares: number; sharePrice: number }>;
    trackTransaction: typeof trackEcommerce;
    trackEventFunction: typeof trackEvent;
  };
  hasInvalidCouponIndices: boolean;
  couponType: CouponType;
};

/**
 * Controller to create wagers.
 *
 * @param createWagerUseCase Use case for creating wagers
 * @param deleteCouponUseCase Use case for deleting a coupon
 * @param deleteCouponWalletUseCase Use case for deleting a coupon wallet
 * @param updateCouponWalletUseCase Use case for updating a coupon wallet
 * @returns Controller executor function
 */
// eslint-disable-next-line @typescript-eslint/max-params
export function createWagerController(
  createWagerUseCase: ICreateWagerUseCase,
  deleteCouponUseCase: IDeleteCouponUseCase,
  deleteCouponWalletUseCase: IDeleteCouponWalletUseCase,
  updateCouponWalletUseCase: IUpdateCouponWalletUseCase
) {
  return async function controller({
    wallets,
    couponId,
    couponIndices,
    showDialog,
    paymentContext,
    hasInvalidCouponIndices,
    couponType,
  }: ControllerArgs): ReturnType<ICreateWagerUseCase['execute']> {
    const wagerResponse = await createWagerUseCase.execute({
      wallets,
      couponId,
      couponIndices,
      showDialog,
      paymentContext,
    });

    const { unpaidItemsCount, paidItemsCouponIndices } = wagerResponse;

    const paidCouponIndices =
      couponType === 'singlerows' && unpaidItemsCount === 0
        ? couponIndices.toReversed()
        : paidItemsCouponIndices.toReversed();
    for (const couponIndex of paidCouponIndices) {
      // If last coupon only reset and don't delete. Prevent bug where outcomes disappear in the UI after placing a bet
      if (hasInvalidCouponIndices || couponIndex !== 0) {
        // eslint-disable-next-line no-await-in-loop
        await deleteCouponUseCase.execute({ couponId, couponIndex });
      }
      deleteCouponWalletUseCase.execute({ couponId, couponIndex });
    }

    // Update wallet for unpaid coupons, eg, first coupon i paid but second is not, we need to update wallet to follow index of coupons to avoid mismatch of wallet and coupon index in the store
    if (unpaidItemsCount > 0 && wallets && Object.keys(wallets).length > 0) {
      const unpaidCouponIndices = couponIndices.filter((index) => !paidItemsCouponIndices.includes(index));
      for (const [index, unpaidCouponIndex] of unpaidCouponIndices.entries()) {
        const wallet = wallets[unpaidCouponIndex] ?? 'WALLET_PLAYER';
        updateCouponWalletUseCase.execute({ couponId, couponIndex: index as CouponIndex, payload: wallet });
        // delete old wallet with unpaidCouponIndex to avoid mismatch of wallet and coupon index in the store
        deleteCouponWalletUseCase.execute({ couponId, couponIndex: unpaidCouponIndex });
      }
    }

    return wagerResponse;
  };
}
